import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  ViewChild,
  inject,
} from '@angular/core';
import { Highlightable } from '@angular/cdk/a11y';
import {
  SELECT_PARENT_COMPONENT,
  SelectParentComponent,
} from '../select-input/select-input.config';

@Component({
  selector: 'et-atoms-select-option',
  templateUrl: './select-option.component.html',
  styleUrls: ['./select-option.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class SelectOptionComponent implements Highlightable, AfterViewInit {
  private elementRef = inject(ElementRef);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private parent = inject<SelectParentComponent>(SELECT_PARENT_COMPONENT, {
    optional: true,
  });

  @Input() value?: string | number | undefined | boolean;
  displayValue: string | undefined;

  @HostBinding('class.disabled')
  @Input()
  disabled = false;

  @Input() hideCheckbox = false;

  @ViewChild('content')
  innerContent!: ElementRef;

  @HostBinding('class.active') active = false;

  @HostBinding('class.hidden')
  _hidden = false;

  get hidden(): boolean {
    return this._hidden;
  }

  /** Whether the wrapping component is in multiple selection mode. */
  @HostBinding('class.multiple')
  get multiple() {
    return this.parent && this.parent.multiple;
  }

  private _selected = false;
  /** Whether or not the option is currently selected. */
  get selected(): boolean {
    return this._selected;
  }

  @HostBinding('class.selected')
  public get isOptionSelected(): boolean {
    return this.parent?.selectedOption === this;
  }

  @HostListener('click', ['$event']) onSelect(event: UIEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.parent?.onSelect(this);
  }

  ngAfterViewInit(): void {
    const displayValue = this.innerContent.nativeElement.innerText;
    if (displayValue) {
      this.displayValue = displayValue;
      this.changeDetectorRef.detectChanges();
    }
  }

  /** Selects the option. */
  select(): void {
    if (!this._selected) {
      this._selected = true;
      this.changeDetectorRef.markForCheck();
    }
  }

  /** Deselects the option. */
  deselect(): void {
    if (this._selected) {
      this._selected = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Sets focus on the element and active property to true
   *
   * @memberOf SelectOptionComponent
   */
  setActiveStyles(): void {
    this.focus();
    this.active = true;
  }

  /**
   * Sets active property to false
   *
   * @memberOf SelectOptionComponent
   */
  setInactiveStyles(): void {
    this.active = false;
  }

  /**
   * Returns value of the option
   *
   * @memberOf SelectOptionComponent
   */
  getLabel(): string {
    return this.value?.toString() as string;
  }

  /**
   * Should set focus state on native element
   * @param {FocusOptions} options - focus options
   * @memberOf SelectOptionComponent
   */
  focus(options?: FocusOptions) {
    this.elementRef.nativeElement.focus(options);
  }

  hide(): void {
    this._hidden = true;
  }

  show(): void {
    this._hidden = false;
  }

  scrollIntoView(options?: ScrollIntoViewOptions) {
    this.elementRef.nativeElement.scrollIntoView(options);
  }
}
